import axios from "@/utils/axios";
import type {
  ApiInsolvenciesResponseType,
  ApiInsolvencyResponseType,
  GetInsolvenciesFilters,
  InsolvencyAPIPayload,
} from "@/types/entities/insolvencies";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

// Simple property type for temporary use until properties api integration is complete
type SimpleProperty = {
  id: string;
  name: string;
};

type ApiPropertiesResponseType = {
  data: SimpleProperty[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  links?: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
};

/**
 * Get paginated list of insolvencies for a specific user
 * @param userId - The user ID to filter insolvencies
 * @param filters - Additional filters (pagination, search, sort, etc.)
 */
export const useGetInsolvencies = (
  userId: string,
  filters: Pick<GetInsolvenciesFilters, "per_page"> = {},
) => {
  const queryParams = {
    per_page: filters.per_page || 50,
    filter: {
      applicant_id: userId,
    },
  };

  return useQuery({
    queryKey: ["insolvencies", queryParams],
    queryFn: async (): Promise<ApiInsolvenciesResponseType> => {
      const response = await axios.get("/admin/v1/insolvencies", {
        params: {
          ...queryParams,
          relationships: "insolvencyable,comments,comments.creator",
        },
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    enabled: !!userId,
    staleTime: 0,
  });
};

/**
 * Get paginated list of insolvencies with infinite scroll support
 * @param userId - The user ID to filter insolvencies
 * @param filters - Additional filters (pagination, search, sort, etc.)
 */
export const useGetInsolvenciesInfinite = (
  userId: string,
  filters: Pick<GetInsolvenciesFilters, "per_page"> = {},
) => {
  const queryParams = {
    per_page: filters.per_page || 10,
    filter: {
      applicant_id: userId,
    },
  };

  return useInfiniteQuery({
    queryKey: ["insolvencies-infinite", queryParams],
    queryFn: async ({
      pageParam = 1,
    }): Promise<ApiInsolvenciesResponseType> => {
      const response = await axios.get("/admin/v1/insolvencies", {
        params: {
          ...queryParams,
          page: pageParam,
          relationships: "insolvencyable,comments,comments.creator",
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
      return undefined;
    },
    enabled: !!userId,
    initialPageParam: 1,
    refetchOnWindowFocus: true,
  });
};

/**
 * Get a single insolvency by ID
 * @param id - The insolvency ID
 */
export const useGetInsolvency = (id: string) => {
  return useQuery({
    queryKey: ["insolvency", id],
    queryFn: async (): Promise<ApiInsolvencyResponseType> => {
      const response = await axios.get(`/admin/v1/insolvencies/${id}`);
      return response.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

/**
 * Create a new insolvency
 */
export const useCreateInsolvency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InsolvencyAPIPayload) => {
      const response = await axios.post("/admin/v1/insolvencies", data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate insolvencies list for the specific user
      queryClient.invalidateQueries({
        queryKey: ["insolvencies", variables.insolvencyable_id],
      });
      // Also invalidate all insolvencies queries to be safe
      queryClient.invalidateQueries({
        queryKey: ["insolvencies"],
      });
    },
  });
};

/**
 * Update an existing insolvency
 * @param id - The insolvency ID to update
 */
export const useUpdateInsolvency = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InsolvencyAPIPayload) => {
      const response = await axios.patch(`/admin/v1/insolvencies/${id}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific insolvency
      queryClient.invalidateQueries({
        queryKey: ["insolvency", id],
      });
      // Invalidate insolvencies list for the specific user
      queryClient.invalidateQueries({
        queryKey: ["insolvencies", variables.insolvencyable_id],
      });
      // Also invalidate all insolvencies queries
      queryClient.invalidateQueries({
        queryKey: ["insolvencies"],
      });
      queryClient.invalidateQueries({
        queryKey: ["insolvencies-infinite"],
      });
    },
  });
};

/**
 * Soft delete (deactivate) an insolvency by changing its status
 * @param id - The insolvency ID to deactivate
 */
export const useDeactivateInsolvency = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data?: {
      status?: string;
      comments?: string;
      reason?: string;
    }) => {
      const response = await axios.patch(`/admin/v1/insolvencies/${id}`, {
        status: data?.status || "CANCELLED",
        comments: data?.comments,
        reason: data?.reason || "",
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the specific insolvency
      queryClient.invalidateQueries({
        queryKey: ["insolvency", id],
      });
      // Invalidate all insolvencies queries
      queryClient.invalidateQueries({
        queryKey: ["insolvencies"],
      });
      queryClient.invalidateQueries({
        queryKey: ["insolvencies-infinite"],
      });
    },
  });
};

/**
 * Cancel an insolvency
 * @param id - The insolvency ID to cancel
 */
export const useResolveInsolvency = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await axios.patch(`/admin/v1/insolvencies/${id}`, {
        status: "CANCELLED",
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the specific insolvency
      queryClient.invalidateQueries({
        queryKey: ["insolvency", id],
      });
      // Invalidate all insolvencies queries
      queryClient.invalidateQueries({
        queryKey: ["insolvencies"],
      });
    },
  });
};

/**
 * Hard delete an insolvency (if needed in the future)
 * @param id - The insolvency ID to delete
 */
export const useDeleteInsolvency = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await axios.delete(`/admin/v1/insolvencies/${id}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all insolvencies queries
      queryClient.invalidateQueries({
        queryKey: ["insolvencies"],
      });
    },
  });
};

/**
 * Reject an appeal for an insolvency (internal user action)
 * @param id - The insolvency ID whose appeal is being rejected
 */
export const useRejectAppeal = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { reason: string; comments: string }) => {
      const response = await axios.patch(`/admin/v1/insolvencies/${id}`, {
        status: "APPEAL_REJECTED",
        reason: data.reason,
        comments: data.comments,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["insolvency", id] });
      queryClient.invalidateQueries({ queryKey: ["insolvencies"] });
      queryClient.invalidateQueries({ queryKey: ["insolvencies-infinite"] });
    },
  });
};

/**
 * Download an appeal document
 * @param fileId - The file ID to download
 * @param fileName - The name of the file
 */
export const downloadAppealDocument = async (
  fileId: string,
  fileName: string,
): Promise<void> => {
  try {
    const response = await axios.get(`/admin/v1/download?file=${fileId}`, {
      responseType: "blob",
    });

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

/**
 * Get properties associated with an applicant
 * @param applicantId - The applicant ID
 */
export const useGetApplicantProperties = (applicantId: string) => {
  return useQuery({
    queryKey: ["applicant-properties", applicantId],
    queryFn: async (): Promise<ApiPropertiesResponseType> => {
      const response = await axios.get(
        `/admin/v1/applicant/${applicantId}/properties`,
      );
      return response.data;
    },
    enabled: !!applicantId,
    refetchOnWindowFocus: false,
  });
};
